/**
 * The only way to send a member off-surface.
 *
 * A bare anchor is load-bearing, not a style: inside a partner WebView the
 * host intercepts `window.open` as internal navigation before an anchor's
 * `target` can fire, so a programmatic open traps the member in the app frame
 * with no way back. Non-http schemes (instagram://, fb://) fail the same way.
 * Render an anchor, attach no navigating onClick, and pass an http(s) URL.
 */
import React, {ReactNode, MouseEvent} from "react";

interface ExternalLinkProps {
    href: string;
    children: ReactNode;
    className?: string;
    /** Side effects only — analytics, marking a claim. NEVER navigation. */
    onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
    "aria-label"?: string;
    title?: string;
    role?: string;
}

/**
 * http(s) only — the guard exists to refuse `javascript:` and `data:`, not to
 * be stricter than the schema it renders. The backend validates these fields as
 * "http/https" (`validateContentBlocks.ts`), so an https-only test silently
 * dropped links the API had already accepted: a product card went unclickable,
 * a retailer row vanished from the drawer, a purchase CTA became plain text.
 */
export function isExternalHref(href?: string): boolean {
    if (!href) return false;
    const scheme = href.slice(0, href.indexOf(":") + 1).toLowerCase();
    return scheme === "https:" || scheme === "http:";
}

export const ExternalLink: React.FC<ExternalLinkProps> = ({
    href, children, className, onClick, ...rest
}) => {
    if (!isExternalHref(href)) return null;

    /*
     * RTTR's proven Realm escape (`ffd46d9022`, confirmed by Realm's app team 2026-05-27):
     * a bare target="_blank" anchor with no navigating onClick. Never intercept the tap.
     */
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            onClick={onClick}
            {...rest}
        >
            {children}
        </a>
    );
};

export default ExternalLink;
